import {
  CarbonSavingsHeader,
  IRRHeader,
  NetworkAvailabilityHeader,
  OnSiteTariffHeader,
  RevenueHeader,
  RevenuePerAreaHeader,
  RooftopLeasedHeader,
  SiteHeader,
  SiteStatusHeader,
  TenantsHeader,
} from "./LandlordSiteListHeaderTooltip";
import { HeaderComponentProps, TableHeaderCell } from "./TableHeaderCell";

interface HeaderData {
  columnName: string;
  sortValue: string;
  HeaderComponent: React.FC<HeaderComponentProps>;
}

const LandlordSiteListHeader = ({
  mode,
  handleColumnClick,
  sortColumn,
  orgAreaUnit,
  displayInRangeScoreCol,
  allSitesNotStarted,
  sortAscending,
  showTenantColumn,
}: {
  mode: string;
  handleColumnClick: (columnName: string) => void;
  sortColumn: string;
  orgAreaUnit: string;
  displayInRangeScoreCol: boolean;
  allSitesNotStarted: boolean;
  sortAscending: boolean;
  showTenantColumn: boolean;
}) => {
  if (mode === "lease") {
    return (
      <LandlordSiteListHeaderLease
        orgAreaUnit={orgAreaUnit}
        displayInRangeScoreCol={displayInRangeScoreCol}
        mode={mode}
        handleColumnClick={handleColumnClick}
        sortColumn={sortColumn}
        sortAscending={sortAscending}
        allSitesNotStarted={allSitesNotStarted}
        showTenantColumn={showTenantColumn}
      />
    );
  }
  return (
    <LandlordSiteListHeaderLicense
      displayInRangeScoreCol={displayInRangeScoreCol}
      handleColumnClick={handleColumnClick}
      sortColumn={sortColumn}
      sortAscending={sortAscending}
      allSitesNotStarted={allSitesNotStarted}
    />
  );
};

const LandlordSiteListHeaderLicense = ({
  displayInRangeScoreCol,
  allSitesNotStarted,
  handleColumnClick,
  sortColumn,
  sortAscending,
}: {
  displayInRangeScoreCol: boolean;
  allSitesNotStarted: boolean;
  handleColumnClick: (columnName: string) => void;
  sortColumn: string;
  sortAscending: boolean;
}) => {
  if (displayInRangeScoreCol) {
    return (
      <LandlordSiteListHeaderLicenseInrangeScoreTrue
        handleColumnClick={handleColumnClick}
        sortColumn={sortColumn}
        sortAscending={sortAscending}
      />
    );
  }
  return (
    <LandlordSiteListHeaderLicenseInrangeScoreFalse
      handleColumnClick={handleColumnClick}
      sortColumn={sortColumn}
      sortAscending={sortAscending}
      allSitesNotStarted={allSitesNotStarted}
    />
  );
};

const LandlordSiteListHeaderLease = ({
  orgAreaUnit,
  displayInRangeScoreCol,
  allSitesNotStarted,
  sortColumn,
  sortAscending,
  handleColumnClick,
  showTenantColumn,
}: {
  orgAreaUnit: string;
  displayInRangeScoreCol: boolean;
  allSitesNotStarted: boolean;
  mode: string;
  sortColumn: string;
  sortAscending: boolean;
  handleColumnClick: (columnName: string) => void;
  showTenantColumn: boolean;
}) => {
  if (displayInRangeScoreCol) {
    return (
      <LandlordSiteListHeaderLeaseInrangeScoreTrue
        handleColumnClick={handleColumnClick}
        sortColumn={sortColumn}
        sortAscending={sortAscending}
        showTenantColumn={showTenantColumn}
        orgAreaUnit={orgAreaUnit}
      />
    );
  }
  return (
    <LandlordSiteListHeaderLeaseInrangeScoreFalse
      handleColumnClick={handleColumnClick}
      sortColumn={sortColumn}
      sortAscending={sortAscending}
      allSitesNotStarted={allSitesNotStarted}
      orgAreaUnit={orgAreaUnit}
    />
  );
};

export default LandlordSiteListHeader;

const LandlordSiteListHeaderLicenseInrangeScoreTrue = ({
  sortColumn,
  sortAscending,
  handleColumnClick,
}: {
  sortColumn: string;
  sortAscending: boolean;
  handleColumnClick: (columnName: string) => void;
}) => {
  const headerData: HeaderData[] = [
    {
      columnName: "siteStatusLicense",
      sortValue: "siteStatusLicense",
      HeaderComponent: SiteStatusHeader,
    },
    { columnName: "name", sortValue: "name", HeaderComponent: SiteHeader },
    { columnName: "irr", sortValue: "irr", HeaderComponent: IRRHeader },
    {
      columnName: "onsiteTariff",
      sortValue: "onsiteTariff",
      HeaderComponent: OnSiteTariffHeader,
    },
    {
      columnName: "licenseRevenue",
      sortValue: "licenseRevenue",
      HeaderComponent: RevenueHeader,
    },
    {
      columnName: "carbonSavings",
      sortValue: "carbonSavings",
      HeaderComponent: CarbonSavingsHeader,
    },
  ];

  return (
    <tr>
      {headerData.map((header) => (
        <TableHeaderCell
          key={header.columnName}
          handleColumnClick={handleColumnClick}
          columnName={header.columnName}
          sortColumn={sortColumn}
          sortAscending={sortAscending}
          sortValue={header.sortValue}
          HeaderComponent={header.HeaderComponent}
          displayInRangeScoreCol={true}
        />
      ))}
      <th></th>
    </tr>
  );
};

const LandlordSiteListHeaderLicenseInrangeScoreFalse = ({
  handleColumnClick,
  sortColumn,
  sortAscending,
  allSitesNotStarted,
}: {
  handleColumnClick: (columnName: string) => void;
  sortColumn: string;
  sortAscending: boolean;
  allSitesNotStarted: boolean;
}) => {
  const headerData: HeaderData[] = [
    { columnName: "name", sortValue: "name", HeaderComponent: SiteHeader },
    {
      columnName: "networkAvailabilityLicense",
      sortValue: "networkAvailabilityLicense",
      HeaderComponent: NetworkAvailabilityHeader,
    },
    { columnName: "irr", sortValue: "irr", HeaderComponent: IRRHeader },
    {
      columnName: "onsiteTariff",
      sortValue: "onsiteTariff",
      HeaderComponent: OnSiteTariffHeader,
    },
    {
      columnName: "licenseRevenue",
      sortValue: "licenseRevenue",
      HeaderComponent: RevenueHeader,
    },
    {
      columnName: "carbonSavings",
      sortValue: "carbonSavings",
      HeaderComponent: CarbonSavingsHeader,
    },
  ];

  if (!allSitesNotStarted) {
    headerData.unshift({
      columnName: "siteStatusLicense",
      sortValue: "siteStatusLicense",
      HeaderComponent: SiteStatusHeader,
    });
  }

  return (
    <tr>
      {headerData.map((header) => (
        <TableHeaderCell
          key={header.columnName}
          handleColumnClick={handleColumnClick}
          columnName={header.columnName}
          sortColumn={sortColumn}
          sortAscending={sortAscending}
          sortValue={header.sortValue}
          HeaderComponent={header.HeaderComponent}
          displayInRangeScoreCol={false}
        />
      ))}
      <th></th>
    </tr>
  );
};

const LandlordSiteListHeaderLeaseInrangeScoreTrue = ({
  handleColumnClick,
  sortColumn,
  sortAscending,
  showTenantColumn,
  orgAreaUnit,
}: {
  handleColumnClick: (columnName: string) => void;
  sortColumn: string;
  sortAscending: boolean;
  showTenantColumn: boolean;
  orgAreaUnit: string;
}) => {
  const headerData: HeaderData[] = [
    {
      columnName: "siteStatusLease",
      sortValue: "siteStatusLease",
      HeaderComponent: SiteStatusHeader,
    },
    { columnName: "name", sortValue: "name", HeaderComponent: SiteHeader },
    {
      columnName: "leaseRevenue",
      sortValue: "leaseRevenue",
      HeaderComponent: RevenueHeader,
    },
    {
      columnName: "leaseRevenuePerArea",
      sortValue: "leaseRevenuePerArea",
      HeaderComponent: RevenuePerAreaHeader,
    },
    {
      columnName: "tenants",
      sortValue: "tenants",
      HeaderComponent: TenantsHeader,
    },
    {
      columnName: "carbonSavings",
      sortValue: "carbonSavings",
      HeaderComponent: CarbonSavingsHeader,
    },
  ];

  if (!showTenantColumn) {
    headerData.splice(4, 1);
  }

  return (
    <tr>
      {headerData.map((header) => (
        <TableHeaderCell
          key={header.columnName}
          handleColumnClick={handleColumnClick}
          columnName={header.columnName}
          sortColumn={sortColumn}
          sortAscending={sortAscending}
          sortValue={header.sortValue}
          HeaderComponent={header.HeaderComponent}
          displayInRangeScoreCol={true}
          orgAreaUnit={orgAreaUnit}
        />
      ))}
      <th>{/* View Site */}</th>
    </tr>
  );
};

const LandlordSiteListHeaderLeaseInrangeScoreFalse = ({
  handleColumnClick,
  sortColumn,
  sortAscending,
  allSitesNotStarted,
  orgAreaUnit,
}: {
  handleColumnClick: (columnName: string) => void;
  sortColumn: string;
  sortAscending: boolean;
  allSitesNotStarted: boolean;
  orgAreaUnit: string;
}) => {
  const headerData: HeaderData[] = [
    { columnName: "name", sortValue: "name", HeaderComponent: SiteHeader },
    {
      columnName: "networkAvailabilityLease",
      sortValue: "networkAvailabilityLease",
      HeaderComponent: NetworkAvailabilityHeader,
    },
    {
      columnName: "roofArea",
      sortValue: "roofArea",
      HeaderComponent: RooftopLeasedHeader,
    },
    {
      columnName: "leaseRevenue",
      sortValue: "leaseRevenue",
      HeaderComponent: RevenueHeader,
    },
    {
      columnName: "leaseRevenuePerArea",
      sortValue: "leaseRevenuePerArea",
      HeaderComponent: RevenuePerAreaHeader,
    },
    {
      columnName: "carbonSavings",
      sortValue: "carbonSavings",
      HeaderComponent: CarbonSavingsHeader,
    },
  ];

  if (!allSitesNotStarted) {
    headerData.unshift({
      columnName: "siteStatusLease",
      sortValue: "siteStatusLease",
      HeaderComponent: SiteStatusHeader,
    });
  }

  return (
    <tr>
      {headerData.map((header) => (
        <TableHeaderCell
          key={header.columnName}
          handleColumnClick={handleColumnClick}
          columnName={header.columnName}
          sortColumn={sortColumn}
          sortAscending={sortAscending}
          sortValue={header.sortValue}
          HeaderComponent={header.HeaderComponent}
          displayInRangeScoreCol={false}
          orgAreaUnit={orgAreaUnit}
        />
      ))}
      <th>{/* View Site */}</th>
    </tr>
  );
};
